import { PayrollDriverRow } from '@/lib/api/company/timesheet/groupedTimesheet/types'
import { getOutOfDateCauseKey } from '@/modules/drivers/shared/timesheetOutOfDateCause'
import { Checkbox } from '@/ui'
import * as React from 'react'
import { formatAmount } from '../format'
import { FaIcon, faIdBadge, faPencilSolid } from '@/lib/fa'
import clx from 'classnames'
import { MoneyInput } from './MoneyInput'
import {
  faExclamation,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import {
  getDisplayedNetto,
  getEditedFields,
  getExpectedNettoBeforeDomesticTrips,
  getNettoMismatchForDisplay,
  isAmountEdited,
  isDomesticTripIncluded,
  pickReward,
} from '../utils'
import { TooltipOptional } from '../elements/TooltipOptional'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const EditableMoneyCell: React.FC<React.ComponentProps<typeof MoneyInput>> = (
  props,
) => (
  <div className="tw-group tw-flex tw-w-full tw-items-center tw-gap-1.5">
    <MoneyInput
      {...props}
      size={8}
      className={clx(
        'tw-w-full tw-min-w-0 tw-flex-1 tw-bg-transparent tw-text-sm tw-text-content-base tw-outline-none',
        props.className,
      )}
    />
    <FaIcon
      icon={faPencilSolid}
      className="tw-flex-shrink-0 tw-text-xs tw-text-primary tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100"
    />
  </div>
)

type Props = {
  displayBailiffDeductionAmount?: boolean
  displayEquivalent?: boolean
  row: PayrollDriverRow
  expectedNetto?: number
  expectedNettoBeforeTrips?: number
  includeBusinessTrip?: boolean
  offPay?: number
  sickPay?: number
  finalLeaveHours?: number
  benefits: number
  bonus: number
  reward?: number
  tripCosts?: number
  mandat?: number
  zaliczka?: number
  handleChange?: React.ChangeEventHandler<HTMLInputElement>
  dynamicLeaveDayTypes?: number[]
  leaveAdditions?: Record<number, number>
}

export const PayrollResultsTableRow = ({
  displayBailiffDeductionAmount,
  displayEquivalent,
  row,
  expectedNetto,
  expectedNettoBeforeTrips,
  includeBusinessTrip,
  offPay,
  finalLeaveHours,
  benefits,
  bonus,
  sickPay,
  reward,
  tripCosts,
  mandat,
  zaliczka,
  handleChange,
  dynamicLeaveDayTypes = [],
  leaveAdditions,
}: Props) => {
  const { tSafe } = useLocalize()
  // Jedno zrodlo prawdy z licznikiem "Przelicz zmodyfikowanych" (getEditedFields):
  // czerwona komorka = wiersz, ktory ten przycisk faktycznie przeliczy.
  const {
    offPayDirty,
    sickPayDirty,
    expectedNettoDirty,
    benefitsDirty,
    bonusIsDirty,
    includeBusinessTripDirty,
    rewardDirty,
    finalLeaveHoursDirty,
    leaveAdditionsDirty,
  } = React.useMemo(
    () =>
      getEditedFields(row, {
        offPay,
        sickPay,
        benefits,
        bonus,
        expectedNetto,
        reward,
        finalLeaveHours,
        includeBusinessTrip,
        leaveAdditions,
      }),
    [
      row,
      offPay,
      sickPay,
      benefits,
      bonus,
      expectedNetto,
      reward,
      finalLeaveHours,
      includeBusinessTrip,
      leaveAdditions,
    ],
  )

  const includeBusinessTripChecked = isDomesticTripIncluded(
    row,
    includeBusinessTrip,
  )

  const driverIsDirty =
    offPayDirty ||
    sickPayDirty ||
    expectedNettoDirty ||
    benefitsDirty ||
    bonusIsDirty ||
    includeBusinessTripDirty ||
    rewardDirty ||
    finalLeaveHoursDirty ||
    leaveAdditionsDirty

  const cellClass =
    'tw-min-w-[100px] tw-px-3 tw-py-4 tw-text-sm tw-text-content-base hover:tw-bg-[#E4E9F7]/50'

  // Nagroda nie domknela wyplaty do oczekiwanego netto. Przy niezapisanej edycji celu
  // znacznik chowamy - wyswietlany cel przestal odpowiadac temu, co policzono.
  const nettoMismatch = React.useMemo(
    () => (expectedNettoDirty ? null : getNettoMismatchForDisplay(row)),
    [row, expectedNettoDirty],
  )
  const nettoMismatchLabel = tSafe('timesheet.netto-mismatch-tooltip')

  // Powod uniewaznienia doklejamy przed ogolnym komunikatem - uzytkownik ma od razu
  // wiedziec, gdzie szukac zmiany (delegacje / aktywnosci / typ dnia / grafik).
  const outOfDateCauseKey = getOutOfDateCauseKey(row.modificationType)
  const outOfDateCause = outOfDateCauseKey ? tSafe(outOfDateCauseKey) : ''
  const outOfDateTooltip = [outOfDateCause, tSafe('timesheet.out-of-date-tooltip')]
    .filter(Boolean)
    .join(' ')

  // Kwota celu sprzed odjecia delegacji krajowych - zaznaczenie "Wliczaj" nadpisuje to, co
  // uzytkownik wpisal, wiec pokazujemy tez wartosc pierwotna.
  const nettoBeforeTrips = React.useMemo(
    () =>
      getExpectedNettoBeforeDomesticTrips(row, {
        expectedNetto,
        includeBusinessTrip,
        captured: expectedNettoBeforeTrips,
      }),
    [row, expectedNetto, includeBusinessTrip, expectedNettoBeforeTrips],
  )

  return (
    <tr className="tw-border-b">
      <td className={cellClass} data-openreplay-masked>
        <div className="tw-flex tw-items-center tw-justify-start tw-gap-2 tw-text-nowrap">
          {driverIsDirty ? (
            <FaIcon
              icon={faExclamation}
              className="tw-text-6 tw-text-red-400"
            />
          ) : (
            <FaIcon icon={faIdBadge} className="tw-text-6" />
          )}
          {row.driver.fullName}
          {/*
            Ewidencja policzona, ale zrodlo (najczesciej delegacje) zmienilo sie pozniej.
            Bez tego znacznika wiersz wyglada na aktualny, a KOSZTY i NAGRODA sa migawka
            sprzed zmiany - to wlasnie stad braly sie zawyzone nagrody przy zerowych kosztach.
          */}
          {row.isOutOfDate && (
            <TooltipOptional tooltip={outOfDateTooltip} maxWidth="22rem">
              <FaIcon
                icon={faTriangleExclamation}
                className="tw-text-6 tw-text-amber-600"
              />
            </TooltipOptional>
          )}
        </div>
      </td>
      {displayBailiffDeductionAmount && (
        <td className={cellClass}>
          {formatAmount(row.bailiffDeductionAmount)}
        </td>
      )}
      {displayEquivalent && (
        <td className={cellClass}>
          <EditableMoneyCell
            data-driver-id={row.driver.id}
            name="finalLeaveHours"
            value={
              typeof finalLeaveHours === 'number'
                ? finalLeaveHours
                : row.finalLeavesGrossHours || 0
            }
            onChange={handleChange}
            isDirty={finalLeaveHoursDirty}
          />
        </td>
      )}
      {displayEquivalent && (
        <td className={cellClass}>
          {formatAmount(row.finalLeavesGrossAmount)}
        </td>
      )}
      <td className={cellClass}>
        <EditableMoneyCell
          data-driver-id={row.driver.id}
          name="offPay"
          value={
            typeof offPay === 'number' ? offPay : row.leavesGrossAmount || 0
          }
          onChange={handleChange}
          isDirty={offPayDirty}
        />
      </td>
      <td className={cellClass}>
        <EditableMoneyCell
          data-driver-id={row.driver.id}
          name="sickPay"
          value={
            typeof sickPay === 'number'
              ? sickPay
              : row.sickLeavesGrossAmount || 0
          }
          onChange={handleChange}
          isDirty={sickPayDirty}
        />
      </td>
      <td className={cellClass}>
        <EditableMoneyCell
          data-driver-id={row.driver.id}
          name="benefits"
          value={
            typeof benefits === 'number'
              ? benefits
              : row.benefitsGrossAmount || 0
          }
          onChange={handleChange}
          isDirty={benefitsDirty}
        />
      </td>
      <td className={cellClass}>
        <EditableMoneyCell
          data-driver-id={row.driver.id}
          name="bonus"
          value={typeof bonus === 'number' ? bonus : row.bonus || 0}
          onChange={handleChange}
          isDirty={bonusIsDirty}
        />
      </td>
      <td
        className={clx(cellClass, {
          'tw-text-red-500': typeof tripCosts === 'number',
        })}
      >
        {formatAmount(
          typeof tripCosts === 'number'
            ? tripCosts
            : (row.tripCosts ?? row.additionAmount),
        )}
      </td>
      {/** deductionAmount */}
      <td className={clx(cellClass, 'tw-min-w-fit')}>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          {formatAmount(row.polishTripsAmount)}
          <label className="tw-mb-0 tw-flex tw-cursor-pointer tw-items-center tw-justify-start tw-gap-2">
            <Checkbox
              data-driver-id={row.driver.id}
              name="includeBusinessTrip"
              checked={includeBusinessTripChecked}
              onChange={handleChange}
            />
            <span
              className={clx({
                'tw-text-red-400': includeBusinessTripDirty,
              })}
            >
              Wliczaj
            </span>
          </label>
        </div>
      </td>
      <td
        className={clx(
          cellClass,
          'tw-relative tw-border-x tw-border-[#1f2937]',
        )}
      >
        <EditableMoneyCell
          data-driver-id={row.driver.id}
          name="nettoAmount"
          value={
            typeof expectedNetto === 'number'
              ? expectedNetto
              : row.expectedNettoAmount || 0.0
          }
          onChange={handleChange}
          isDirty={expectedNettoDirty}
        />
        {/*
          Kwota sprzed odjecia delegacji siedzi POZA przeplywem (absolute), tuz pod kwota celu.
          W jednej linii sie nie miesci: input (size=8, ~65px) + nawias (~58px) + olowek +
          padding to ~170px przy kolumnie ~120px, wiec kazdy uklad inline rozpychal kolumne.
          Absolutne pozycjonowanie nie dodaje ani szerokosci kolumnie, ani wysokosci wierszowi -
          miesci sie w wolnym miejscu ponizej, ktore i tak zostawia py-4 komorki.
        */}
        {nettoBeforeTrips == null ? null : (
          <span className="tw-absolute tw-bottom-1 tw-left-3 tw-leading-none">
            <TooltipOptional
              tooltip={tSafe('timesheet.netto-before-trips-tooltip')}
            >
              <span className="tw-whitespace-nowrap tw-text-[10px] tw-leading-none tw-text-content-muted">
                ({formatAmount(nettoBeforeTrips)})
              </span>
            </TooltipOptional>
          </span>
        )}
      </td>
      <td className={cellClass}>
        {nettoMismatch == null ? (
          formatAmount(getDisplayedNetto(row))
        ) : (
          <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-amber-600">
            <TooltipOptional
              tooltip={`${nettoMismatchLabel} Różnica: ${
                nettoMismatch > 0 ? '+' : ''
              }${formatAmount(nettoMismatch)}`}
            >
              <FaIcon icon={faTriangleExclamation} className="tw-text-6" />
            </TooltipOptional>
            {formatAmount(getDisplayedNetto(row))}
          </span>
        )}
      </td>
      <td className={cellClass}>
        <EditableMoneyCell
          data-driver-id={row.driver.id}
          name="reward"
          value={typeof reward === 'number' ? reward : pickReward(row)}
          onChange={handleChange}
          isDirty={rewardDirty}
        />
      </td>
      <td className={cellClass}>{formatAmount(row.summary)}</td>
      <td className={cellClass}>
        {typeof mandat === 'number' ? formatAmount(mandat) : '-'}
      </td>
      <td className={cellClass}>
        {typeof zaliczka === 'number' ? formatAmount(zaliczka) : '-'}
      </td>
      {dynamicLeaveDayTypes.map((dt) => {
        const entry = row.timesheetAdditionForLeaves?.find(
          (e) => e.dayType === dt,
        )
        const hasEntry = entry != null
        const override = leaveAdditions?.[dt]
        const isOverride = typeof override === 'number'
        const baseAmount = entry?.amount ?? 0
        const value = isOverride ? override : baseAmount
        const isCellDirty = isAmountEdited(override, baseAmount)
        return (
          <td key={dt} className={cellClass}>
            {hasEntry ? (
              <EditableMoneyCell
                data-driver-id={row.driver.id}
                data-day-type={dt}
                name="leaveAddition"
                value={value}
                onChange={handleChange}
                isDirty={isCellDirty}
              />
            ) : (
              '-'
            )}
          </td>
        )
      })}
    </tr>
  )
}
